import { Outlet } from 'react-router-dom';
import { DashboardSidebar } from './DashboardSidebar';
import { DashboardTopbar } from './DashboardTopbar';

/**
 * Top-level dashboard chrome.
 *
 * Layout contract (single source of truth — do not duplicate in pages):
 *   - `<main>` owns the **scroll surface** and keeps its scrollbar pinned to
 *     the viewport edge (no `max-w` on `<main>` itself).
 *   - The inner wrapper applies the **canonical page gutter + width cap** —
 *     `px-6 py-6` for the 24px viewport gutter and `mx-auto max-w-screen-2xl`
 *     so content centers and stops growing past 1536px on wide displays.
 *
 * The cap was 1280px (`max-w-7xl`) until issue #178. Data-dense pages could
 * not fit their columns in it: the GPU explorer gives 320px of that budget to
 * its configuration sidebar, leaving ~910px for a nine-column table, which
 * silently clipped two columns behind a horizontal scrollbar 25 rows below
 * the headers they belonged to. On a 1990px display the page was leaving
 * ~700px of empty gutter while hiding data.
 *
 * Because this lives at the layout level, every routed page automatically
 * inherits the same margins/centering whether or not it uses `PageContainer`.
 * This is why the tabbed views (ProjectLayout, DeploymentDashboardPage) now
 * align flush with the rest of the dashboard — no extra wrappers required.
 *
 * Pages that need full-bleed (e.g. a future map view) can opt out by
 * placing `-mx-6 max-w-none` (or similar) on their root.
 */
export function DashboardLayout() {
  return (
    <div className="flex h-full bg-background text-foreground">
      <DashboardSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <DashboardTopbar />
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-screen-2xl px-6 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
